if (!customElements.get('withdrawal-form')) {
  const ERROR_MESSAGES = {
    no_matching_order:
      'Nie znaleźliśmy zamówienia o tym numerze i nazwisku. Sprawdź dane i spróbuj ponownie.',
    outside_window: 'Minął 14-dniowy termin na odstąpienie od umowy dla tego zamówienia.',
    already_cancelled: 'To zamówienie zostało już anulowane.',
    order_missing_email:
      'Nie mogliśmy przetworzyć zgłoszenia automatycznie. Skontaktuj się z obsługą klienta.',
    retry: 'Nie udało się połączyć. Spróbuj ponownie za chwilę.',
    unknown: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
  }

  const KNOWN_ERROR_CODES = [
    'no_matching_order',
    'outside_window',
    'already_cancelled',
    'order_missing_email',
  ]

  class WithdrawalForm extends HTMLElement {
    constructor() {
      super()

      // Krok 1
      this.form1 = this.querySelector('[data-withdrawal-step="enter"]')
      this.submitButton = this.querySelector('[data-withdrawal-submit]')
      this.errorBox = this.querySelector('[data-withdrawal-error]')
      this.errorText = this.querySelector('[data-withdrawal-error-text]')

      // Krok 2
      this.step2 = this.querySelector('[data-withdrawal-step="select"]')
      this.orderNameInput = this.querySelector('[data-order-name]')
      this.customerNameInput = this.querySelector('[data-customer-name]')
      this.wholeOrderCheckbox = this.querySelector('[data-whole-order]')
      this.lineItemsContainer = this.querySelector('[data-line-items]')
      this.lineItemTpl = this.querySelector('[data-line-item-tpl]')
      this.errorBoxSelect = this.querySelector('[data-withdrawal-error-select]')
      this.errorTextSelect = this.querySelector('[data-withdrawal-error-text-select]')
      this.backButton = this.querySelector('[data-withdrawal-back]')
      this.submitSelectButton = this.querySelector('[data-withdrawal-submit-select]')
      this.statementCheckbox = this.querySelector('[data-statement-confirmed]')

      // Potwierdzenie
      this.successBox = this.querySelector('[data-withdrawal-success]')

      // Endpointy
      this.lookupEndpoint = this.dataset.lookupEndpoint
      this.submitEndpoint = this.dataset.endpoint

      // Bind
      this.onSubmit1 = this.onSubmit1.bind(this)
      this.onSubmit2 = this.onSubmit2.bind(this)
      this.onBack = this.onBack.bind(this)
      this.onWholeOrderChange = this.onWholeOrderChange.bind(this)
    }

    connectedCallback() {
      if (this.form1) this.form1.addEventListener('submit', this.onSubmit1)
      if (this.backButton) this.backButton.addEventListener('click', this.onBack)
      if (this.wholeOrderCheckbox)
        this.wholeOrderCheckbox.addEventListener('change', this.onWholeOrderChange)
      if (this.submitSelectButton)
        this.submitSelectButton.addEventListener('click', this.onSubmit2)
    }

    disconnectedCallback() {
      if (this.form1) this.form1.removeEventListener('submit', this.onSubmit1)
      if (this.backButton) this.backButton.removeEventListener('click', this.onBack)
      if (this.wholeOrderCheckbox)
        this.wholeOrderCheckbox.removeEventListener('change', this.onWholeOrderChange)
      if (this.submitSelectButton)
        this.submitSelectButton.removeEventListener('click', this.onSubmit2)
    }

    async onSubmit1(event) {
      event.preventDefault()
      this.hideError()

      const orderName = (this.form1.elements['orderName']?.value || '').trim()
      const customerName = (this.form1.elements['customerName']?.value || '').trim()

      if (!orderName) {
        this.showError('Podaj numer zamówienia.')
        return
      }
      if (!customerName) {
        this.showError('Podaj imię i nazwisko.')
        return
      }

      this.setLoading(this.submitButton, true)
      try {
        const result = await this.doLookup(orderName, customerName)
        if (result.ok) {
          if (result.lineItems.length === 0) {
            this.showError(ERROR_MESSAGES.no_matching_order)
            return
          }
          this.renderStep2(orderName, customerName, result.lineItems)
        } else {
          this.showError(ERROR_MESSAGES[result.code] || ERROR_MESSAGES.unknown)
        }
      } catch (error) {
        console.error('[withdrawal-form] lookup threw', error)
        this.showError(ERROR_MESSAGES.retry)
      } finally {
        this.setLoading(this.submitButton, false)
      }
    }

    async doLookup(orderName, customerName) {
      const endpoint = (this.lookupEndpoint || '').trim()

      if (!endpoint) {
        console.warn('[withdrawal-form] No lookup endpoint — simulating success.')
        return { ok: true, lineItems: [] }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderName,
          customerName,
          locale: this.dataset.locale || 'pl',
        }),
      })

      return this.parseLookupResponse(response)
    }

    async parseLookupResponse(response) {
      if (response.status === 200) {
        let body = null
        try {
          body = await response.json()
        } catch (_) {}
        const lineItems = Array.isArray(body?.lineItems)
          ? body.lineItems.filter(this.isValidLineItem)
          : []
        return { ok: true, lineItems }
      }

      let body = null
      try {
        body = await response.json()
      } catch (_) {}

      if (response.status === 422 && body && typeof body.error === 'string') {
        return {
          ok: false,
          code: KNOWN_ERROR_CODES.includes(body.error) ? body.error : 'unknown',
        }
      }
      if (response.status === 502 || response.status === 503) {
        return { ok: false, code: 'retry' }
      }
      return { ok: false, code: 'unknown' }
    }

    isValidLineItem(item) {
      return (
        item &&
        typeof item.lineItemId === 'string' &&
        typeof item.title === 'string' &&
        typeof item.quantity === 'number' &&
        item.quantity >= 1
      )
    }

    renderStep2(orderName, customerName, lineItems) {
      if (this.orderNameInput) this.orderNameInput.value = orderName
      if (this.customerNameInput) this.customerNameInput.value = customerName

      if (this.wholeOrderCheckbox) this.wholeOrderCheckbox.checked = true
      if (this.statementCheckbox) this.statementCheckbox.checked = false

      this.renderLineItems(lineItems)

      if (this.form1) this.form1.hidden = true
      if (this.step2) this.step2.hidden = false
    }

    renderLineItems(lineItems) {
      if (!this.lineItemsContainer || !this.lineItemTpl) return
      this.lineItemsContainer.innerHTML = ''

      lineItems.forEach((item, i) => {
        const clone = this.lineItemTpl.content.cloneNode(true)
        const li = clone.querySelector('[data-line-item]')
        li.dataset.lineItemId = item.lineItemId
        li.dataset.maxQuantity = String(item.quantity)

        const checkbox = clone.querySelector('[data-item-selected]')
        checkbox.id = `withdrawal-item-selected-${i}`
        checkbox.checked = true
        checkbox.disabled = true
        checkbox.setAttribute('aria-label', item.title)

        const img = clone.querySelector('[data-item-image]')
        const placeholder = clone.querySelector('[data-item-placeholder]')
        if (item.imageUrl) {
          img.src = item.imageUrl
          img.alt = item.title
          if (placeholder) placeholder.hidden = true
        } else {
          img.hidden = true
        }

        const titleEl = clone.querySelector('[data-item-title]')
        if (titleEl) titleEl.textContent = item.title

        const variantEl = clone.querySelector('[data-item-variant]')
        if (variantEl) {
          variantEl.textContent = item.variantTitle || ''
          variantEl.hidden = !item.variantTitle
        }

        const qtyInput = clone.querySelector('[data-item-qty]')
        if (qtyInput) {
          qtyInput.id = `withdrawal-item-qty-${i}`
          qtyInput.max = String(item.quantity)
          qtyInput.value = String(item.quantity)
          qtyInput.disabled = true
        }

        const qtyLabel = clone.querySelector('[data-item-qty-label]')
        if (qtyLabel) qtyLabel.setAttribute('for', `withdrawal-item-qty-${i}`)

        this.lineItemsContainer.appendChild(clone)
      })
    }

    onWholeOrderChange() {
      const isWhole = this.wholeOrderCheckbox?.checked ?? true
      this.lineItemsContainer
        ?.querySelectorAll('[data-item-selected], [data-item-qty]')
        .forEach((el) => {
          el.toggleAttribute('disabled', isWhole)
          if (isWhole && el.type === 'checkbox') el.checked = true
        })
    }

    async onSubmit2() {
      this.hideErrorSelect()

      if (!this.statementCheckbox?.checked) {
        this.showErrorSelect('Aby kontynuować, potwierdź oświadczenie.')
        return
      }

      const wholeOrder = this.wholeOrderCheckbox?.checked ?? true
      let lineItems

      if (wholeOrder) {
        lineItems = 'ALL'
      } else {
        const selected = [
          ...(this.lineItemsContainer?.querySelectorAll('[data-line-item]') || []),
        ]
          .filter((li) => li.querySelector('[data-item-selected]')?.checked)
          .map((li) => ({
            lineItemId: li.dataset.lineItemId,
            quantity: Math.min(
              Math.max(
                parseInt(li.querySelector('[data-item-qty]')?.value || '1', 10),
                1
              ),
              parseInt(li.dataset.maxQuantity || '1', 10)
            ),
          }))

        if (selected.length === 0) {
          this.showErrorSelect('Wybierz co najmniej jedną pozycję do zwrotu.')
          return
        }
        lineItems = selected
      }

      const contactPhone = (
        this.step2?.querySelector('[name="contactPhone"]')?.value || ''
      ).trim()
      const reason = (this.step2?.querySelector('[name="reason"]')?.value || '').trim()

      const payload = {
        orderName: this.orderNameInput?.value || '',
        customerName: this.customerNameInput?.value || '',
        lineItems,
        statementText: this.dataset.statement || '',
        locale: this.dataset.locale || 'pl',
      }
      if (contactPhone) payload.contactPhone = contactPhone
      if (reason) payload.reason = reason

      this.setLoading(this.submitSelectButton, true)
      try {
        const result = await this.submitWithdrawal(payload)
        if (result.ok) {
          this.showSuccess()
        } else {
          this.showErrorSelect(ERROR_MESSAGES[result.code] || ERROR_MESSAGES.unknown)
        }
      } catch (error) {
        console.error('[withdrawal-form] submit threw', error)
        this.showErrorSelect(ERROR_MESSAGES.retry)
      } finally {
        this.setLoading(this.submitSelectButton, false)
      }
    }

    async submitWithdrawal(payload) {
      const endpoint = (this.submitEndpoint || '').trim()

      if (!endpoint) {
        console.warn('[withdrawal-form] No submit endpoint — simulating success.')
        return { ok: true, simulated: true }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return this.parseSubmitResponse(response)
    }

    async parseSubmitResponse(response) {
      if (response.status === 201) return { ok: true }

      let body = null
      try {
        body = await response.json()
      } catch (_) {}

      if (response.status === 422 && body && typeof body.error === 'string') {
        return {
          ok: false,
          code: KNOWN_ERROR_CODES.includes(body.error) ? body.error : 'unknown',
        }
      }
      if (response.status === 502 || response.status === 503) {
        return { ok: false, code: 'retry' }
      }
      return { ok: false, code: 'unknown' }
    }


    onBack() {
      if (this.step2) this.step2.hidden = true
      if (this.form1) this.form1.hidden = false
      this.hideErrorSelect()
    }

    setLoading(button, isLoading) {
      if (!button) return
      button.toggleAttribute('disabled', isLoading)
      button.setAttribute('aria-busy', String(isLoading))
    }

    showError(message) {
      if (!this.errorBox) return
      if (this.errorText) this.errorText.textContent = message
      this.errorBox.hidden = false
    }

    hideError() {
      if (this.errorBox) this.errorBox.hidden = true
    }

    showErrorSelect(message) {
      if (!this.errorBoxSelect) return
      if (this.errorTextSelect) this.errorTextSelect.textContent = message
      this.errorBoxSelect.hidden = false
    }

    hideErrorSelect() {
      if (this.errorBoxSelect) this.errorBoxSelect.hidden = true
    }

    showSuccess() {
      if (this.form1) this.form1.hidden = true
      if (this.step2) this.step2.hidden = true
      if (this.successBox) {
        this.successBox.hidden = false
        this.successBox.focus()
      }
    }
  }

  customElements.define('withdrawal-form', WithdrawalForm)
}
